<template>
  <div class="music-container">
    <!-- 全屏音乐播放特效 -->
    <div class="music-section">
      <!-- 悬浮的音频播放器控制 -->
      <div class="player-controls" v-show="showControls">
        <button @click="goBack" class="btn-primary">← 返回</button>
        <button @click="toggleListen" class="btn-primary">{{ isListening ? '停止听音乐' : '开始听音乐' }}</button>
        <button
          @click="toggleVisualizationMode"
          class="btn-primary"
          :disabled="!isListening"
        >切换模式 (当前: {{ visualizationMode === 'bars' ? '柱状图' : '波形图' }})</button>
        <button @click="toggleImageDisplay" class="btn-primary">{{ showImage ? '隐藏图片' : '显示图片' }}</button>
        <div class="interval-control" v-if="showImage">
          <label>间隔:</label>
          <input type="number" v-model.number="photoInterval" min="0.1" max="10" step="0.1" class="interval-input" />秒
        </div>
        <button @click="toggleFullscreen" class="btn-primary">{{ isFullscreen ? '退出全屏' : '全屏显示' }}</button>
        <div class="status-text" v-if="isListening">
          <span class="pulse-dot"></span>
          正在监听环境声音...
        </div>
      </div>

      <!-- 可视化画布 -->
      <canvas ref="canvas" class="visualizer-canvas" :class="{ 'active': isListening }"></canvas>

      <!-- 图片浮层 -->
      <div class="image-overlay" v-if="imageLayerActive">
        <!-- 多张照片叠加显示 -->
        <div
          v-for="(photo, index) in activePhotos"
          :key="photo.id"
          class="photo-wrapper"
          :style="{ zIndex: 51 + index }"
        >
          <img
            :src="photo.src"
            class="image-display"
            :class="photo.transitionType"
            :style="photo.positionStyle"
          />
        </div>
        <!-- 粒子容器 -->
        <div class="particles-container" ref="particlesContainer"></div>
      </div>

      <!-- 右下角隐藏/显示按钮 -->
      <button class="toggle-controls-btn" @click="showControls = !showControls">
        <span v-if="showControls">👁️</span>
        <span v-else>👁️‍🗨️</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ListenMusic",
  data() {
    return {
      // 图片相关
      images: [
        require("@/image/love/1.jpeg"),
        require("@/image/love/2.jpeg")
        // require("@/image/love/3.jpeg"),
        // require("@/image/love/4.jpeg"),
        // require("@/image/love/5.jpeg"),
        // require("@/image/love/6.jpeg")
      ],
      currentImageIndex: 0,
      showControls: true,
      loadedImages: [],

      // 图片浮层显示控制（多张叠加）
      showImage: false,
      imageLayerActive: false,
      imageAutoSwitchTimer: null,
      activePhotos: [], // 当前活跃显示的照片数组
      photoIdCounter: 0, // 照片ID计数器
      photoInterval: 2, // 照片出现间隔（秒），默认1秒
      photoDuration: 5000, // 每张照片显示时长（毫秒），固定3秒

      // 音频相关
      isListening: false,
      mediaStream: null,

      // 音频可视化
      audioContext: null,
      analyser: null,
      dataArray: null,
      bufferLength: null,
      animationId: null,
      visualizationMode: "bars", // 'bars' 或 'wave'

      // 全屏状态
      isFullscreen: false
    };
  },
  computed: {},
  watch: {
    // 监听间隔变化，重启定时器
    photoInterval(newVal) {
      if (this.showImage && this.imageAutoSwitchTimer) {
        clearInterval(this.imageAutoSwitchTimer);
        this.imageAutoSwitchTimer = setInterval(() => {
          if (this.showImage) {
            this.addNewPhoto();
          }
        }, newVal * 1000);
      }
    }
  },
  mounted() {
    this.preloadImages();
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  },
  methods: {
    // 图片显示控制
    toggleImageDisplay() {
      if (this.showImage) {
        this.hideImage();
      } else {
        this.displayImage();
      }
    },

    displayImage() {
      this.showImage = true;
      this.imageLayerActive = true;
      this.activePhotos = []; // 清空现有照片
      this.photoIdCounter = 0;

      // 立即显示第一张照片
      this.addNewPhoto();

      // 启动定时器：按间隔添加新照片
      if (this.imageAutoSwitchTimer) {
        clearInterval(this.imageAutoSwitchTimer);
      }
      this.imageAutoSwitchTimer = setInterval(() => {
        if (this.showImage) {
          this.addNewPhoto();
        }
      }, this.photoInterval * 1000); // 转换为毫秒
    },

    hideImage() {
      if (this.imageAutoSwitchTimer) {
        clearInterval(this.imageAutoSwitchTimer);
        this.imageAutoSwitchTimer = null;
      }
      this.activePhotos = [];
      this.imageLayerActive = false;
      this.showImage = false;
    },

    addNewPhoto() {
      // 选择下一张图片
      const imageSrc = this.images[this.currentImageIndex];
      this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;

      // 随机选择切换效果
      const transitions = [
        "fade",
        "zoom",
        "slide-left",
        "slide-right",
        "slide-top",
        "slide-bottom",
        "rotate"
      ];
      const transitionType = transitions[Math.floor(Math.random() * transitions.length)];

      // 生成随机位置
      const randomLeft = 15 + Math.random() * 40; // 15-55%
      const randomTop = 15 + Math.random() * 40; // 15-55%
      const positionStyle = {
        left: randomLeft + "%",
        top: randomTop + "%"
      };

      // 创建新照片对象
      const newPhoto = {
        id: this.photoIdCounter++,
        src: imageSrc,
        transitionType: transitionType,
        positionStyle: positionStyle
      };

      // 添加到活跃照片数组（新照片会因为更大的index而有更高的z-index）
      this.activePhotos.push(newPhoto);

      // 创建粒子效果
      this.createParticles();

      // 3秒后移除此照片
      setTimeout(() => {
        const index = this.activePhotos.findIndex(p => p.id === newPhoto.id);
        if (index > -1) {
          this.activePhotos.splice(index, 1);
        }
      }, this.photoDuration);
    },



    preloadImages() {
      this.images.forEach((url, index) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          this.loadedImages[index] = img;
        };
        img.src = url;
      });
    },

    createParticles() {
      const container = this.$refs.particlesContainer;
      if (!container) return;

      container.innerHTML = "";

      const particleCount = 30;
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement("div");
        particle.className =
          Math.random() > 0.5 ? "particle-heart" : "particle-petal";
        particle.style.left = Math.random() * 100 + "%";
        particle.style.top = Math.random() * 100 + "%";
        particle.style.animationDelay = Math.random() * 2 + "s";
        particle.style.animationDuration = 3 + Math.random() * 2 + "s";
        container.appendChild(particle);

        setTimeout(() => {
          particle.remove();
        }, 5000);
      }
    },

    handleResize() {},

    goBack() {
      this.$router.back();
    },

    // 全屏相关方法
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        this.$el.requestFullscreen().catch(err => {
          console.error("无法进入全屏模式:", err);
        });
      } else {
        document.exitFullscreen();
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },

    //返回上一级
    goBack() {
      this.$router.back();
    },

    // 音频处理方法
    async toggleListen() {
      if (this.isListening) {
        this.stopListening();
      } else {
        await this.startListening();
      }
    },
    async startListening() {
      try {
        // 获取麦克风权限
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        // 初始化音频上下文
        this.initMicrophoneContext();

        this.isListening = true;
        this.startVisualization();
      } catch (error) {
        console.error("无法访问麦克风:", error);
        alert("无法访问麦克风，请检查浏览器权限设置");
      }
    },
    stopListening() {
      this.isListening = false;

      // 停止动画
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      // 停止媒体流
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }

      // 清空画布
      const canvas = this.$refs.canvas;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    },

    // 音频可视化
    initMicrophoneContext() {
      if (this.audioContext) {
        this.audioContext.close();
      }

      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;

      const source = this.audioContext.createMediaStreamSource(
        this.mediaStream
      );
      source.connect(this.analyser);

      this.bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(this.bufferLength);
    },
    toggleVisualizationMode() {
      this.visualizationMode =
        this.visualizationMode === "bars" ? "wave" : "bars";
    },
    toggleControls() {
      this.showControls = !this.showControls;
    },
    startVisualization() {
      if (!this.analyser) return;

      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");

      // 设置canvas尺寸
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      const draw = () => {
        this.animationId = requestAnimationFrame(draw);

        this.analyser.getByteFrequencyData(this.dataArray);

        // 清空画布，使用粉色背景
        ctx.fillStyle = "rgba(255, 192, 203, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 添加动态爱心背景粒子（当图片显示时减少数量）
        const time = Date.now() * 0.001;
        const heartCount = this.showImage ? 6 : 12; // 图片显示时减半
        for (let i = 0; i < heartCount; i++) {
          const x = (Math.sin(time * 0.5 + i * 0.5) * 0.4 + 0.5) * canvas.width;
          const y =
            (Math.cos(time * 0.3 + i * 0.7) * 0.4 + 0.5) * canvas.height;
          const size = 15 + Math.sin(time * 2 + i) * 5;

          // 绘制爱心
          ctx.save();
          ctx.translate(x, y);
          ctx.scale(size / 15, size / 15);

          ctx.beginPath();
          ctx.moveTo(0, 0);
          // 左半心
          ctx.bezierCurveTo(-10, -8, -20, -2, -20, 8);
          ctx.bezierCurveTo(-20, 15, -10, 22, 0, 28);
          // 右半心
          ctx.bezierCurveTo(10, 22, 20, 15, 20, 8);
          ctx.bezierCurveTo(20, -2, 10, -8, 0, 0);
          ctx.closePath();

          const hue = 320 + Math.sin(time + i) * 40;
          ctx.fillStyle = `hsla(${hue}, 75%, 65%, 0.12)`;
          ctx.fill();
          ctx.restore();
        }

        if (this.visualizationMode === "bars") {
          this.drawBars(ctx, canvas);
        } else {
          this.drawWave(ctx, canvas);
        }
      };

      draw();
    },
    drawBars(ctx, canvas) {
      const barWidth = (canvas.width / this.bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < this.bufferLength; i++) {
        const barHeight = (this.dataArray[i] / 255) * canvas.height * 0.85;

        // 彩虹渐变色，每个柱子不同颜色，使用简单渐变
        const hue = (i / this.bufferLength) * 360;
        const gradient = ctx.createLinearGradient(
          0,
          canvas.height,
          0,
          canvas.height - barHeight
        );
        gradient.addColorStop(0, `hsla(${hue}, 85%, 60%, 1)`);
        gradient.addColorStop(1, `hsla(${hue + 20}, 90%, 65%, 1)`);

        // 绘制主柱子 - 清晰实色，无阴影
        ctx.shadowBlur = 0;
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 2, barHeight);

        x += barWidth + 1;
      }
    },
    drawWave(ctx, canvas) {
      const sliceWidth = canvas.width / this.bufferLength;

      // 绘制2层波形（减少层数提高清晰度）
      for (let layer = 0; layer < 2; layer++) {
        ctx.lineWidth = 3.5 - layer * 0.5;
        ctx.shadowBlur = 6 - layer * 2;

        ctx.beginPath();
        let x = 0;

        for (let i = 0; i < this.bufferLength; i++) {
          const v = this.dataArray[i] / 255.0;
          const y = (v * canvas.height) / 2 + canvas.height / 4 + layer * 40;

          // 彩虹渐变描边 - 提高不透明度
          const hue = (i / this.bufferLength) * 360 + layer * 45;
          ctx.strokeStyle = `hsla(${hue}, 100%, ${70 - layer * 5}%, ${0.95 -
            layer * 0.15})`;
          ctx.shadowColor = `hsla(${hue}, 100%, 70%, 0.3)`;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            // 使用贝塞尔曲线使波形更平滑
            const prevX = x - sliceWidth;
            const prevY = ctx.currentY || y;
            const cpX = prevX + sliceWidth / 2;
            const cpY = (prevY + y) / 2;
            ctx.quadraticCurveTo(cpX, prevY, x, y);
            ctx.currentY = y;
          }

          x += sliceWidth;
        }

        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.stroke();

        // 添加填充效果
        if (layer === 0) {
          ctx.lineTo(canvas.width, canvas.height);
          ctx.lineTo(0, canvas.height);
          ctx.closePath();
          const fillGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
          fillGradient.addColorStop(0, "hsla(280, 100%, 70%, 0.1)");
          fillGradient.addColorStop(1, "hsla(320, 100%, 60%, 0.05)");
          ctx.fillStyle = fillGradient;
          ctx.fill();
        }
      }

      // 添加粒子效果
      for (let i = 0; i < this.bufferLength; i += 5) {
        const v = this.dataArray[i] / 255.0;
        if (v > 0.6) {
          const x = (i / this.bufferLength) * canvas.width;
          const y = (v * canvas.height) / 2 + canvas.height / 4;
          const hue = (i / this.bufferLength) * 360;

          ctx.beginPath();
          ctx.arc(x, y, 2 + v * 4, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${hue}, 100%, 80%, ${v})`;
          ctx.shadowBlur = 10;
          ctx.shadowColor = `hsla(${hue}, 100%, 70%, 0.8)`;
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      }
    }
  },
  beforeDestroy() {
    if (this.imageAutoSwitchTimer) {
      clearInterval(this.imageAutoSwitchTimer);
    }
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.stopListening();
    if (this.audioContext) {
      this.audioContext.close();
    }
  }
};
</script>

<style scoped lang="less">
.music-container {
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
  overflow: hidden;
}

// 音乐区域 - 全屏
.music-section {
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffb3d9 0%, #ffc4e1 50%, #ffd1e8 100%);
  overflow: hidden;
}

// 图片浮层
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
  pointer-events: none;

  .photo-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .image-display {
    position: absolute;
    max-width: 50%;
    max-height: 50%;
    border-radius: 20px;
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4), 0 8px 25px rgba(0, 0, 0, 0.3),
      0 3px 10px rgba(0, 0, 0, 0.2);
    object-fit: contain;
    transform-origin: center;
    filter: drop-shadow(0 5px 15px rgba(255, 120, 200, 0.2));
    animation-fill-mode: both;

    // 淡入效果
    &.fade {
      animation: fadeIn 0.8s ease-out;
    }

    // 缩放效果
    &.zoom {
      animation: zoomIn 0.8s ease-out;
    }

    // 左侧滑入
    &.slide-left {
      animation: slideInLeft 0.8s ease-out;
    }

    // 右侧滑入
    &.slide-right {
      animation: slideInRight 0.8s ease-out;
    }

    // 顶部滑入
    &.slide-top {
      animation: slideInTop 0.8s ease-out;
    }

    // 底部滑入
    &.slide-bottom {
      animation: slideInBottom 0.8s ease-out;
    }

    // 旋转效果
    &.rotate {
      animation: rotateIn 0.8s ease-out;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes zoomIn {
    from {
      opacity: 0;
      transform: scale(0.3);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInTop {
    from {
      opacity: 0;
      transform: translateY(-100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInBottom {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes rotateIn {
    from {
      opacity: 0;
      transform: rotate(360deg) scale(0.5);
    }
    to {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }

  .particles-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: hidden;
  }

  .particle-heart,
  .particle-petal {
    position: absolute;
    width: 20px;
    height: 20px;
    opacity: 0.6;
    animation: float 5s ease-in-out forwards;
  }

  .particle-heart {
    background: linear-gradient(45deg, #ff6b9d, #ff8fab);
    transform: rotate(45deg);

    &::before,
    &::after {
      content: "";
      position: absolute;
      width: 20px;
      height: 20px;
      background: inherit;
      border-radius: 50%;
    }

    &::before {
      top: -10px;
      left: 0;
    }

    &::after {
      left: 10px;
      top: 0;
    }
  }

  .particle-petal {
    background: linear-gradient(135deg, #ffd6e8, #ffc4dd);
    border-radius: 50% 0 50% 0;
    transform: rotate(0deg);
  }

  @keyframes float {
    0% {
      transform: translateY(0) rotate(0deg) scale(1);
      opacity: 0;
    }
    10% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.8;
    }
    100% {
      transform: translateY(-100px) rotate(360deg) scale(0.3);
      opacity: 0;
    }
  }
}

.player-controls {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  z-index: 10;
  padding: 15px 25px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
}

.interval-control {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;

  label {
    color: rgba(255, 255, 255, 0.9);
  }

  .interval-input {
    width: 60px;
    padding: 6px 10px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    color: white;
    font-size: 14px;
    text-align: center;
    outline: none;
    transition: all 0.3s ease;

    &:focus {
      border-color: rgba(255, 255, 255, 0.6);
      background: rgba(255, 255, 255, 0.25);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      opacity: 1;
    }
  }
}

.btn-primary,
.btn-nav {
  padding: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 5px 20px rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.1);
  }
}

.status-text {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #00f2ff;
  font-size: 14px;
  font-weight: 500;
  margin-left: 15px;

  .pulse-dot {
    width: 8px;
    height: 8px;
    background: #ff4444;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

.visualizer-canvas {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #ffc4dd 0%, #ffb3d9 50%, #ffd1e8 100%);
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  &.active {
    filter: brightness(1.1);
  }
}

.toggle-controls-btn {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
    box-shadow: 0 5px 20px rgba(0, 242, 255, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }
}
</style>